import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Variants are picked by INTENT, not by how they look.
 *
 * `primary` is the one main affirmative action on a view. `secondary` is
 * neutral — an outline with ink text, never a coloured fill, so it cannot be
 * mistaken for the primary. `danger` is destructive and is the same variant
 * everywhere that destructive action appears, trigger and confirm alike; a
 * "Could not deliver" that is red in the action bar and brand-green in the
 * sheet is a bug.
 */
const button = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap',
    'font-semibold rounded-full select-none',
    'transition-[background-color,color,border-color,box-shadow,transform]',
    'duration-[var(--motion-duration-fast)] ease-standard',
    'active:scale-[0.985]',
    // Disabled is a real state: dimmed and inert. Loading is NOT this — see
    // the `loading` prop below.
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary text-on-primary',
          'hover:bg-primary-hover active:bg-primary-active',
          'shadow-xs hover:shadow-lift',
        ],
        secondary: [
          'bg-raised text-ink border border-line-strong',
          'hover:bg-sunken hover:border-line-bold active:bg-muted',
        ],
        ghost: ['text-ink hover:bg-muted active:bg-muted'],
        danger: [
          'bg-danger text-on-danger',
          'hover:bg-danger-hover active:bg-danger-hover',
          'shadow-xs',
        ],
        // A destructive action that should not shout until it is chosen.
        'danger-ghost': ['text-danger-ink hover:bg-error-surface active:bg-error-surface'],
        link: ['text-link underline underline-offset-4 hover:text-ink rounded-sm px-0'],
      },
      size: {
        // Every size clears the 24x24 minimum by a wide margin; `md` and up
        // clear the 44x44 recommended for a primary action. `xl` is the one
        // pinned action at the bottom of a delivery — see tokens/foundation.
        sm: 'h-control-sm px-4 text-sm [&_svg]:size-4',
        md: 'h-control-md px-5 text-[0.9375rem] [&_svg]:size-[1.125rem]',
        lg: 'h-control-lg px-7 text-base [&_svg]:size-5',
        xl: 'h-control-xl px-8 text-lg [&_svg]:size-[1.375rem]',
        icon: 'size-control-md p-0 [&_svg]:size-[1.125rem]',
        'icon-sm': 'size-control-sm p-0 [&_svg]:size-4',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof button> & {
    asChild?: boolean;
    /**
     * In flight. The control keeps FULL strength and swaps its label for a
     * spinner — reusing the disabled dimming would read as "you cannot do
     * this" rather than "this is happening", which on the one button that
     * records a handover is a meaningful difference.
     */
    loading?: boolean;
    loadingLabel?: string;
  };

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  loading = false,
  loadingLabel,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      type={asChild ? undefined : type}
      className={cn(button({ variant, size, block }), className)}
      // Still clickable-looking, but not actually re-submittable.
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      data-loading={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {/* Announced, so a screen reader hears the change rather than
              silence where the label used to be. */}
          <span>{loadingLabel ?? 'Working'}</span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}
